import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  NoSymbolIcon,
  ChartBarIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { notificationAPI } from '../../services/api';
import { NOTIFICATION_EVENT_TYPES } from '../../constants/notificationEventTypes';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface NotificationLog {
  id: string;
  eventType: string;
  entityType: string;
  entityId: string;
  ruleId: string;
  templateId: string;
  recipientEmail: string;
  recipientType: string;
  ticketNumber?: string;
  status: string;
  sentAt?: string;
  error?: string;
  metadata?: any;
  createdAt: string;
  NotificationRule?: {
    name: string;
  };
  EmailTemplate?: {
    name: string;
    subject: string;
  };
}

interface NotificationStats {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  skipped: number;
  successRate: number;
}

function getNotificationEventLabel(eventType: string): string {
  return NOTIFICATION_EVENT_TYPES.find((e) => e.value === eventType)?.label ?? eventType;
}

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending', icon: ClockIcon, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  { value: 'SENT', label: 'Sent', icon: CheckCircleIcon, color: 'text-green-600', bgColor: 'bg-green-100' },
  { value: 'FAILED', label: 'Failed', icon: XCircleIcon, color: 'text-red-600', bgColor: 'bg-red-100' },
  { value: 'SKIPPED', label: 'Skipped', icon: NoSymbolIcon, color: 'text-gray-600', bgColor: 'bg-gray-100' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200];

/** Filter keys → raw recipientType values stored in log metadata */
const SENDING_TO_FILTER_ALIASES: Record<string, string[]> = {
  ASSIGNEE: ['ASSIGNEE', 'ASSIGNED_TO'],
  ASSIGNMENT_GROUP: ['GROUP_MEMBERS', 'GROUP_MEMBER', 'ASSIGNMENT_GROUP'],
  CALLER: ['CALLER'],
  CONTACT: ['CONTACT', 'CONTACT_EMAILS', 'CONTACT_EMAIL'],
  CUSTOMER: ['CUSTOMER'],
  CREATED_BY: ['CREATED_BY'],
  RESOLVED_BY: ['RESOLVED_BY'],
  CLOSED_BY: ['CLOSED_BY'],
  CUSTOM: ['CUSTOM'],
  REQUESTER: ['REQUESTER'],
  APPROVAL_GROUP: ['APPROVAL_GROUP_MEMBERS', 'APPROVAL_GROUP_MEMBER'],
  KB_OWNER: ['KB_OWNER'],
  AUTHOR: ['AUTHOR'],
  OWNER: ['OWNER'],
};

const SENDING_TO_FILTER_OPTIONS = [
  { value: 'ASSIGNEE', label: 'Assignee' },
  { value: 'ASSIGNMENT_GROUP', label: 'Assignment Group' },
  { value: 'CALLER', label: 'Caller' },
  { value: 'CONTACT', label: 'Contact' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CREATED_BY', label: 'Created By' },
  { value: 'RESOLVED_BY', label: 'Resolved By' },
  { value: 'CLOSED_BY', label: 'Closed By' },
  { value: 'CUSTOM', label: 'Custom Email' },
  { value: 'REQUESTER', label: 'Requester' },
  { value: 'APPROVAL_GROUP', label: 'Approval Group' },
  { value: 'KB_OWNER', label: 'KB Owner' },
  { value: 'AUTHOR', label: 'Author' },
  { value: 'OWNER', label: 'Owner (Time Entry)' },
];

function formatRecipientType(recipientType: string | null): string {
  if (!recipientType) return 'N/A';

  for (const [key, aliases] of Object.entries(SENDING_TO_FILTER_ALIASES)) {
    if (aliases.includes(recipientType)) {
      return SENDING_TO_FILTER_OPTIONS.find((o) => o.value === key)?.label || recipientType;
    }
  }

  return recipientType;
}

/** HTML date (YYYY-MM-DD) → start of that calendar day in local time, as UTC ISO for the API */
function dateInputToStartIso(dateStr: string): string | undefined {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return undefined;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/** HTML date (YYYY-MM-DD) → end of that calendar day in local time, as UTC ISO for the API */
function dateInputToEndIso(dateStr: string): string | undefined {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return undefined;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

const NotificationLogsManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearch = searchParams.get('search') ?? '';
  const filterStatus = searchParams.get('status') ?? '';
  const filterEventType = searchParams.get('eventType') ?? '';
  const filterStartDate = searchParams.get('dateFrom') ?? '';
  const filterEndDate = searchParams.get('dateTo') ?? '';
  const filterRecipient = searchParams.get('recipient') ?? '';
  const filterTicket = searchParams.get('ticket') ?? '';
  const filterSendingTo = searchParams.get('sendingTo') ?? '';

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const [totalCount, setTotalCount] = useState(0);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize]
  );

  const eventTypeFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const et of NOTIFICATION_EVENT_TYPES) {
      if (!seen.has(et.value)) seen.set(et.value, et.label);
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, []);

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filterStatus, filterEventType, filterStartDate, filterEndDate, filterRecipient, filterTicket, filterSendingTo, appliedSearch, page, pageSize]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (filterStatus) params.status = filterStatus;
      if (filterEventType) params.eventType = filterEventType;
      const searchTerm = filterTicket || appliedSearch;
      if (searchTerm) params.search = searchTerm;
      if (filterRecipient) params.recipientEmail = filterRecipient;
      if (filterSendingTo) params.recipientType = filterSendingTo;
      const dateFromIso = dateInputToStartIso(filterStartDate);
      const dateToIso = dateInputToEndIso(filterEndDate);
      if (dateFromIso) params.dateFrom = dateFromIso;
      if (dateToIso) params.dateTo = dateToIso;

      const response = await notificationAPI.logs.list(params);
      const result = response.data.data || {};

      setLogs(result.logs || []);
      setTotalCount(typeof result.total === 'number' ? result.total : Number(result.total) || 0);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch notification logs');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params: Record<string, string> = {};
      const dateFromIso = dateInputToStartIso(filterStartDate);
      const dateToIso = dateInputToEndIso(filterEndDate);
      if (dateFromIso) params.dateFrom = dateFromIso;
      if (dateToIso) params.dateTo = dateToIso;
      const searchTerm = filterTicket || appliedSearch;
      if (searchTerm) params.search = searchTerm;
      if (filterRecipient) params.recipientEmail = filterRecipient;
      if (filterSendingTo) params.recipientType = filterSendingTo;

      const response = await notificationAPI.logs.stats(params);
      setStats(response.data.data || null);
    } catch (error: any) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleRetry = async (log: NotificationLog) => {
    try {
      setRetrying(log.id);
      await notificationAPI.logs.retry(log.id);
      showSuccess('Notification retry initiated successfully');
      fetchLogs();
      fetchStats();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to retry notification');
      console.error('Error:', error);
    } finally {
      setRetrying(null);
    }
  };

  const handleClearLogs = async () => {
    try {
      setClearing(true);
      const response = await notificationAPI.logs.clearAll();
      const count = response.data.count || 0;
      showSuccess(`Successfully cleared ${count} notification log(s)`);
      setShowClearConfirm(false);
      fetchLogs();
      fetchStats();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to clear notification logs');
      console.error('Error:', error);
    } finally {
      setClearing(false);
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      status: null,
      eventType: null,
      dateFrom: null,
      dateTo: null,
      recipient: null,
      ticket: null,
      sendingTo: null,
      page: '1',
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null, page: '1' });
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') {
      patchSearchParams({ search: null, page: '1' });
    }
  };

  const statusFilterOptions = useMemo(
    () => STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label })),
    []
  );

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'dateTime':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterStartDate}
              placeholder="From (YYYY-MM-DD)..."
              onChange={(v) => {
                patchSearchParams({ dateFrom: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'event':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={eventTypeFilterOptions}
              value={filterEventType}
              placeholder="All Events"
              onChange={(v) => {
                patchSearchParams({ eventType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'ticket':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterTicket}
              placeholder="Filter by ticket #..."
              onChange={(v) => {
                patchSearchParams({ ticket: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'recipient':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterRecipient}
              placeholder="Filter by email..."
              onChange={(v) => {
                patchSearchParams({ recipient: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'sendingTo':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={SENDING_TO_FILTER_OPTIONS}
              value={filterSendingTo}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ sendingTo: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={statusFilterOptions}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ status: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterStartDate,
      filterEventType,
      filterTicket,
      filterRecipient,
      filterSendingTo,
      filterStatus,
      eventTypeFilterOptions,
      statusFilterOptions,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearch}"`, color: 'blue' });
    }
    if (filterTicket) {
      chips.push({ id: 'ticket', label: 'Ticket', value: filterTicket, color: 'blue' });
    }
    if (filterRecipient) {
      chips.push({ id: 'recipient', label: 'Recipient', value: filterRecipient, color: 'blue' });
    }
    if (filterSendingTo) {
      const label =
        SENDING_TO_FILTER_OPTIONS.find((o) => o.value === filterSendingTo)?.label || filterSendingTo;
      chips.push({ id: 'sendingTo', label: 'Sending To', value: label, color: 'orange' });
    }
    if (filterStatus) {
      const label = STATUS_OPTIONS.find((s) => s.value === filterStatus)?.label || filterStatus;
      chips.push({ id: 'status', label: 'Status', value: label, color: 'purple' });
    }
    if (filterEventType) {
      chips.push({
        id: 'eventType',
        label: 'Event',
        value: getNotificationEventLabel(filterEventType),
        color: 'teal',
      });
    }
    if (filterStartDate) {
      chips.push({ id: 'dateFrom', label: 'From Date', value: filterStartDate, color: 'orange' });
    }
    if (filterEndDate) {
      chips.push({ id: 'dateTo', label: 'To Date', value: filterEndDate, color: 'orange' });
    }
    return chips;
  }, [appliedSearch, filterTicket, filterRecipient, filterSendingTo, filterStatus, filterEventType, filterStartDate, filterEndDate]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'ticket':
        patchSearchParams({ ticket: null, page: '1' });
        break;
      case 'recipient':
        patchSearchParams({ recipient: null, page: '1' });
        break;
      case 'sendingTo':
        patchSearchParams({ sendingTo: null, page: '1' });
        break;
      case 'status':
        patchSearchParams({ status: null, page: '1' });
        break;
      case 'eventType':
        patchSearchParams({ eventType: null, page: '1' });
        break;
      case 'dateFrom':
        patchSearchParams({ dateFrom: null, page: '1' });
        break;
      case 'dateTo':
        patchSearchParams({ dateTo: null, page: '1' });
        break;
    }
  };

  const showSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message: string) => {
    setNotification({ message, type: 'error' });
    setTimeout(() => setNotification(null), 5000);
  };

  const getStatusBadge = (status: string) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status);
    if (!statusOption) return null;

    const Icon = statusOption.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusOption.bgColor} ${statusOption.color}`}>
        <Icon className="h-4 w-4 mr-1" />
        {statusOption.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-3">
      {/* Compact Header Bar (matches Notification Rules / Survey Email Templates admin pages) */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Notification Logs</h1>
              <p className="text-sm text-gray-600">
                View and monitor all notification delivery attempts with detailed status information.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    placeholder="Ticket number or recipient email..."
                    className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                    autoComplete="off"
                  />
                </div>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Search
                </button>
              </form>
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4 mr-1.5" />
                Clear All Logs
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-2 p-3 rounded-md flex-shrink-0 ${
          notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2 flex-shrink-0">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                    <dd className="text-2xl font-semibold text-gray-900">{stats.total || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Sent</dt>
                    <dd className="text-2xl font-semibold text-green-600">{stats.sent || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                    <dd className="text-2xl font-semibold text-red-600">{stats.failed || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-2xl font-semibold text-yellow-600">{stats.pending || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Success Rate</dt>
                    <dd className="text-2xl font-semibold text-blue-600">
                      {typeof stats.successRate === 'number' ? stats.successRate.toFixed(1) : '0.0'}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearFilters}
          />
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-white shadow rounded-lg mb-2">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (Array.isArray(logs) ? logs : []).length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No notification logs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Logs will appear here as notifications are sent.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('dateTime', 'Date & Time')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('event', 'Event')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('ticket', 'Ticket Number')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('recipient', 'Recipient')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('sendingTo', 'Sending To')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      {renderColumnHeader('status', 'Status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(Array.isArray(logs) ? logs : []).map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getNotificationEventLabel(log.eventType)}
                        </div>
                        <div className="text-xs text-gray-500">{log.entityType}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {log.ticketNumber || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{log.recipientEmail}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatRecipientType(log.recipientType)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(log.status)}
                        {log.error && (
                          <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={log.error}>
                            {log.error}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {log.status === 'FAILED' && (
                          <button
                            onClick={() => handleRetry(log)}
                            disabled={retrying === log.id}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center disabled:opacity-50"
                            title="Retry"
                          >
                            <ArrowPathIcon className={`h-5 w-5 mr-1 ${retrying === log.id ? 'animate-spin' : ''}`} />
                            {retrying === log.id ? 'Retrying...' : 'Retry'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination (server-side, matches Notification Rules / Survey Email Templates admin lists) */}
            {!loading && totalCount > 0 && (
              <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      patchSearchParams({ pageSize: e.target.value, page: '1' });
                    }}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="text-sm text-gray-700">
                  Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{' '}
                  {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: '1' })}
                    disabled={page === 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    &laquo;
                  </button>
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: String(page - 1) })}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {(() => {
                    const pages: (number | string)[] = [];

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (page <= 4) {
                        for (let i = 1; i <= 5; i++) pages.push(i);
                        pages.push('...');
                        pages.push(totalPages);
                      } else if (page >= totalPages - 3) {
                        pages.push(1);
                        pages.push('...');
                        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        pages.push('...');
                        for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                        pages.push('...');
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-3 py-1 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => patchSearchParams({ page: String(p) })}
                          className={`px-3 py-1 text-sm border rounded-md ${
                            page === p
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}

                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: String(page + 1) })}
                    disabled={page >= totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => patchSearchParams({ page: String(totalPages) })}
                    disabled={page >= totalPages}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Clear Logs Confirmation Dialog */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Clear All Notification Logs</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Are you sure you want to delete all notification logs? This action cannot be undone and will permanently remove all log records from the database.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearLogs}
                disabled={clearing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {clearing ? 'Clearing...' : 'Clear All Logs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationLogsManagement;
